import React, { useState, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Tooltip,
    Tabs,
    Tab,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import moment from "moment";
import axios from "axios";
import { API, KEYS, AGENTS } from "../../config/env";

const ISRCCallsTable = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [calls, setCalls] = useState([]);
    const [chats, setChats] = useState([]);
    const [newChats, setNewChats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [chatsLoading, setChatsLoading] = useState(false);
    const [chatsLoaded, setChatsLoaded] = useState(false);
    const [selectedCall, setSelectedCall] = useState(null);
    const [selectedChat, setSelectedChat] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [openChatDialog, setOpenChatDialog] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [transcriptLoading, setTranscriptLoading] = useState(false);
    const [transcriptError, setTranscriptError] = useState(null);
    const [openNewChatDialog, setOpenNewChatDialog] = useState(false);
    const [selectedNewChat, setSelectedNewChat] = useState(null);
    const [newChatTranscriptLoading, setNewChatTranscriptLoading] = useState(false);
    const [newChatTranscriptError, setNewChatTranscriptError] = useState(null);

    useEffect(() => {
        fetchCalls();
    }, []);

    useEffect(() => {
        if (activeTab === 1 && !chatsLoaded) {
            fetchChats();
        }
    }, [activeTab, chatsLoaded]);

    const fetchCalls = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${API.CORE}/webcall/v1/get/conversations/${AGENTS.ISRC_CALLS}`
            );
            setCalls(response.data || []);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching ISRC calls:", error);
            setLoading(false);
        }
    };

    const fetchChats = async () => {
        try {
            setChatsLoading(true);
            const [oldRes, newRes] = await Promise.all([
                axios.get(`${API.CORE}/conversation-isrc/v1/sessions`),
                axios.get(`${API.CORE}/webcall/v1/get/chats/${AGENTS.ISRC_CHATS}`),
            ]);
            setChats(oldRes.data || []);
            const sorted = (newRes.data || []).sort((a, b) => b.start_timestamp - a.start_timestamp);
            setNewChats(sorted);
            setChatsLoaded(true);
        } catch (error) {
            console.error("Error fetching ISRC chats:", error);
        } finally {
            setChatsLoading(false);
        }
    };

    const formatDuration = (milliseconds) => {
        const seconds = Math.floor(milliseconds / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    };

    const handleViewTranscript = async (call) => {
        setSelectedCall(call);
        setOpenDialog(true);
        setTranscriptError(null);
        setTranscriptLoading(true);
        try {
            const response = await axios.get(
                `${API.RETELL}/v2/get-call/${call.call_id}`,
                {
                    headers: {
                        Authorization: `Bearer ${KEYS.RETELL_BEARER}`,
                    },
                }
            );
            setSelectedCall({ ...call, transcript: response.data?.transcript });
        } catch (error) {
            console.error("Error fetching call transcript:", error);
            setTranscriptError("Failed to load transcript.");
        } finally {
            setTranscriptLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedCall(null);
        setTranscriptLoading(false);
        setTranscriptError(null);
    };

    const handleViewChat = (chat) => {
        setSelectedChat(chat);
        setOpenChatDialog(true);
    };

    const handleCloseChatDialog = () => {
        setOpenChatDialog(false);
        setSelectedChat(null);
    };

    const handleViewNewChatTranscript = async (chat) => {
        setSelectedNewChat(chat);
        setOpenNewChatDialog(true);
        setNewChatTranscriptError(null);
        setNewChatTranscriptLoading(true);
        try {
            const res = await axios.get(
                `${API.CORE}/webcall/v1/get/chat/${chat.chat_id}`
            );
            setSelectedNewChat({ ...chat, transcript: res.data?.transcript });
        } catch (error) {
            console.error("Error fetching new chat transcript:", error);
            setNewChatTranscriptError("Failed to load transcript.");
        } finally {
            setNewChatTranscriptLoading(false);
        }
    };

    const handleCloseNewChatDialog = () => {
        setOpenNewChatDialog(false);
        setSelectedNewChat(null);
        setNewChatTranscriptLoading(false);
        setNewChatTranscriptError(null);
    };

    const renderChatMessages = (messages) => {
        if (!messages || messages.length === 0) return null;
        return messages.map((message, index) => {
            const isAssistant = message.sender === "assistant";
            return (
                <div
                    key={message._id || index}
                    style={{
                        backgroundColor: isAssistant ? "#f0f9ff" : "#f0fdf4",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        marginBottom: "8px",
                        borderLeft: `4px solid ${isAssistant ? "#3b82f6" : "#10b981"}`,
                        whiteSpace: "pre-wrap",
                    }}
                >
                    <strong style={{ color: isAssistant ? "#1e40af" : "#047857", fontSize: "14px" }}>
                        {isAssistant ? "Assistant:" : "User:"}
                    </strong>
                    <span style={{ marginLeft: "8px" }}>{message.message}</span>
                </div>
            );
        });
    };

    const handleCopyCallId = (callId) => {
        navigator.clipboard.writeText(callId);
        setCopiedId(callId);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const formatTranscript = (transcript) => {
        if (!transcript) return null;

        const lines = transcript.split('\n');
        const messages = [];
        let currentSpeaker = null;
        let currentMessage = [];

        lines.forEach((line) => {
            if (line.startsWith('Agent:')) {
                if (currentSpeaker && currentMessage.length > 0) {
                    messages.push({ speaker: currentSpeaker, text: currentMessage.join(' ') });
                }
                currentSpeaker = 'Agent';
                currentMessage = [line.substring(6).trim()];
            } else if (line.startsWith('User:')) {
                if (currentSpeaker && currentMessage.length > 0) {
                    messages.push({ speaker: currentSpeaker, text: currentMessage.join(' ') });
                }
                currentSpeaker = 'User';
                currentMessage = [line.substring(5).trim()];
            } else if (line.trim() !== '') {
                if (currentSpeaker) currentMessage.push(line.trim());
            }
        });

        if (currentSpeaker && currentMessage.length > 0) {
            messages.push({ speaker: currentSpeaker, text: currentMessage.join(' ') });
        }

        return messages.map((message, index) => {
            if (message.speaker === 'Agent') {
                return (
                    <div
                        key={index}
                        style={{
                            backgroundColor: '#f0f9ff',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            marginBottom: '8px',
                            borderLeft: '4px solid #3b82f6',
                        }}
                    >
                        <strong style={{ color: '#1e40af', fontSize: '14px' }}>Agent:</strong>
                        <span style={{ marginLeft: '8px' }}>{message.text}</span>
                    </div>
                );
            } else if (message.speaker === 'User') {
                return (
                    <div
                        key={index}
                        style={{
                            backgroundColor: '#f0fdf4',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            marginBottom: '8px',
                            borderLeft: '4px solid #10b981',
                        }}
                    >
                        <strong style={{ color: '#047857', fontSize: '14px' }}>User:</strong>
                        <span style={{ marginLeft: '8px' }}>{message.text}</span>
                    </div>
                );
            }
            return null;
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "ended": return "success";
            case "ongoing": return "primary";
            case "missed": return "error";
            default: return "default";
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <CircularProgress />
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1
                    style={{
                        fontFamily: "Nunito Sans, sans-serif",
                        fontSize: "2rem",
                        fontWeight: 700,
                        color: "#18181B",
                    }}
                >
                    {activeTab === 0 ? "Calls" : "Chats"}
                </h1>
            </div>

            <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                style={{ marginBottom: "16px" }}
            >
                <Tab label="Calls" />
                <Tab label="Chats" />
            </Tabs>

            {activeTab === 0 && (
                <TableContainer component={Paper} elevation={2} style={{ maxHeight: '500px', overflow: 'auto' }}>
                    <Table stickyHeader style={{ tableLayout: "fixed" }}>
                        <TableHead>
                            <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                                <TableCell style={{ fontWeight: 700 }}>Call ID</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Start Time</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>End Time</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Duration</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Transcript</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {calls.map((call) => (
                                <TableRow key={call.call_id} hover style={{ cursor: "pointer" }}>
                                    <TableCell>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <span style={{ fontFamily: "monospace", fontSize: "0.85rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, minWidth: 0 }}>
                                                {call.call_id}
                                            </span>
                                            <Tooltip title={copiedId === call.call_id ? "Copied!" : "Copy Call ID"}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); handleCopyCallId(call.call_id); }}
                                                    style={{ padding: "4px", color: copiedId === call.call_id ? "#10b981" : "#6b7280", flexShrink: 0 }}
                                                >
                                                    <ContentCopyIcon style={{ fontSize: "16px" }} />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    </TableCell>
                                    <TableCell>{moment(call.start_timestamp).format("MMM DD, YYYY HH:mm:ss")}</TableCell>
                                    <TableCell>{moment(call.end_timestamp).format("MMM DD, YYYY HH:mm:ss")}</TableCell>
                                    <TableCell>{formatDuration(call.duration_ms)}</TableCell>
                                    <TableCell>
                                        <Button variant="outlined" size="small" onClick={() => handleViewTranscript(call)}>
                                            View
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {activeTab === 1 && (
                chatsLoading ? (
                    <div className="flex items-center justify-center" style={{ minHeight: "300px" }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <TableContainer component={Paper} elevation={2} style={{ maxHeight: '500px', overflow: 'auto' }}>
                        <Table stickyHeader style={{ tableLayout: "fixed" }}>
                            <TableHead>
                                <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                                    <TableCell style={{ fontWeight: 700, width: "420px" }}>Chat ID</TableCell>
                                    <TableCell style={{ fontWeight: 700, paddingLeft: "40px", paddingRight: "40px", whiteSpace: "nowrap" }}>Time</TableCell>
                                    <TableCell style={{ fontWeight: 700, paddingLeft: "40px" }}>Messages</TableCell>
                                    <TableCell style={{ fontWeight: 700, paddingLeft: "40px" }}>Transcript</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {newChats.map((chat) => (
                                    <TableRow key={chat.chat_id} hover style={{ cursor: "pointer" }}>
                                        <TableCell>
                                            <div style={{ display: "flex", alignItems: "center", gap: "32px" }}>
                                                <span style={{ fontFamily: "monospace", fontSize: "0.85rem", flex: 1, minWidth: 0 }}>
                                                    {chat.chat_id}
                                                </span>
                                                <Tooltip title={copiedId === chat.chat_id ? "Copied!" : "Copy Chat ID"}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); handleCopyCallId(chat.chat_id); }}
                                                        style={{ padding: "4px", color: copiedId === chat.chat_id ? "#10b981" : "#6b7280", flexShrink: 0 }}
                                                    >
                                                        <ContentCopyIcon style={{ fontSize: "16px" }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                        <TableCell style={{ paddingLeft: "40px", whiteSpace: "nowrap" }}>{moment(chat.start_timestamp).format("MMM DD, YYYY HH:mm:ss")}</TableCell>
                                        <TableCell style={{ paddingLeft: "40px" }}>{chat.combined_cost ? Math.round(chat.combined_cost * 2 / 3) : 0}</TableCell>
                                        <TableCell style={{ paddingLeft: "40px" }}>
                                            <Button variant="outlined" size="small" onClick={() => handleViewNewChatTranscript(chat)}>
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {chats.map((chat) => (
                                    <TableRow key={chat._id} hover style={{ cursor: "pointer" }}>
                                        <TableCell>
                                            <div style={{ display: "flex", alignItems: "center", gap: "32px" }}>
                                                <span style={{ fontFamily: "monospace", fontSize: "0.85rem", flex: 1, minWidth: 0 }}>
                                                    {chat._id}
                                                </span>
                                                <Tooltip title={copiedId === chat._id ? "Copied!" : "Copy Chat ID"}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); handleCopyCallId(chat._id); }}
                                                        style={{ padding: "4px", color: copiedId === chat._id ? "#10b981" : "#6b7280", flexShrink: 0 }}
                                                    >
                                                        <ContentCopyIcon style={{ fontSize: "16px" }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                        <TableCell style={{ paddingLeft: "40px", paddingRight: "40px", whiteSpace: "nowrap" }}>{moment(chat.createdAt).format("MMM DD, YYYY HH:mm:ss")}</TableCell>
                                        <TableCell style={{ paddingLeft: "40px" }}>{chat.messages ? chat.messages.length : 0}</TableCell>
                                        <TableCell style={{ paddingLeft: "40px" }}>
                                            <Button variant="outlined" size="small" onClick={() => handleViewChat(chat)}>
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )
            )}

            {/* Call transcript dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle style={{ fontWeight: 700 }}>Call Transcript</DialogTitle>
                <DialogContent dividers>
                    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", lineHeight: "1.6" }}>
                        {transcriptLoading ? (
                            <div className="flex items-center justify-center" style={{ padding: "32px 0" }}>
                                <CircularProgress size={28} />
                            </div>
                        ) : transcriptError ? (
                            <p style={{ color: "#dc2626" }}>{transcriptError}</p>
                        ) : selectedCall?.transcript ? (
                            formatTranscript(selectedCall.transcript)
                        ) : (
                            <p>No transcript available for this call.</p>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">Close</Button>
                </DialogActions>
            </Dialog>

            {/* Old chat transcript dialog — unchanged */}
            <Dialog open={openChatDialog} onClose={handleCloseChatDialog} maxWidth="md" fullWidth>
                <DialogTitle style={{ fontWeight: 700 }}>Chat Transcript</DialogTitle>
                <DialogContent dividers>
                    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", lineHeight: "1.6" }}>
                        {renderChatMessages(selectedChat?.messages)}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseChatDialog} color="primary">Close</Button>
                </DialogActions>
            </Dialog>

            {/* New Retell chat transcript dialog */}
            <Dialog open={openNewChatDialog} onClose={handleCloseNewChatDialog} maxWidth="md" fullWidth>
                <DialogTitle style={{ fontWeight: 700 }}>Chat Transcript</DialogTitle>
                <DialogContent dividers>
                    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", lineHeight: "1.6" }}>
                        {newChatTranscriptLoading ? (
                            <div className="flex items-center justify-center" style={{ padding: "32px 0" }}>
                                <CircularProgress size={28} />
                            </div>
                        ) : newChatTranscriptError ? (
                            <p style={{ color: "#dc2626" }}>{newChatTranscriptError}</p>
                        ) : selectedNewChat?.transcript ? (
                            formatTranscript(selectedNewChat.transcript)
                        ) : (
                            <p>No transcript available for this chat.</p>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseNewChatDialog} color="primary">Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ISRCCallsTable;
